import { useEffect, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authApi } from '../api/endpoints.ts';
import { isMfaChallenge, type MfaChallengeResponse } from '@shared/types/index.ts';
import { requestAssertion, wasDismissed, type ServerPublicKeyRequest } from '../lib/webauthn.ts';
import { ApiRequestError } from '../api/client.ts';
import { useSession } from '../state/session.tsx';
import { Alert, Button, Checkbox, TextInput } from '../components/ui.tsx';
import { Turnstile, useAppConfig } from '../components/turnstile.tsx';
import { oauthErrorMessage, SocialSignInButtons } from '../components/social-buttons.tsx';
import { useDocumentTitle } from '../hooks/index.ts';
import AuthShell from '../components/auth-shell.tsx';

/**
 * A `?next=` destination, or undefined for anything that is not one.
 *
 * Parsed against this origin rather than pattern-matched, because a prefix
 * check is not the same question. `/\evil.example` starts with exactly one
 * slash and passes any `startsWith('//')` test — and then the URL parser
 * normalizes the backslash and `location.assign` navigates to
 * `https://evil.example/`, which is an open redirect built out of a check that
 * looked right. Asking the parser where the value actually points cannot be
 * fooled that way. The Worker applies the same rule to social sign-in returns
 * (`safeReturnPath`).
 */
export function sameOriginPath(value: string | null): string | undefined {
  if (!value) return undefined;
  try {
    const url = new URL(value, window.location.origin);
    if (url.origin !== window.location.origin) return undefined;
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return undefined;
  }
}

export default function LoginPage() {
  const { t } = useTranslation();
  useDocumentTitle(t('auth.login.documentTitle'));
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { setSession } = useSession();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  /**
   * Set when the password was right and a second factor is still owed.
   *
   * Holding the challenge in state rather than routing to another page keeps
   * the partial-authentication token out of the URL and out of history — it is
   * short-lived and single-purpose, but a token in an address bar is a token in
   * a screenshot, a bookmark and a referrer header.
   */
  const [challenge, setChallenge] = useState<MfaChallengeResponse | null>(null);
  /**
   * A social sign-in that came back needing a factor.
   *
   * The Worker cannot render into this app, so it redirects here with
   * `?verify=1` and leaves the partial token in an HttpOnly cookie. Without
   * this the visitor would be shown the ordinary password form while holding
   * no session — a dead end that looks like the sign-in silently failing.
   */
  const [resumingSocial, setResumingSocial] = useState(searchParams.get('verify') === '1');
  const [turnstileReset, setTurnstileReset] = useState(0);
  /**
   * The Worker only demands a challenge after repeated failures from an address
   * (spec §6.5), so an ordinary sign-in stays one click. The widget appears once
   * an attempt has failed — including the first attempt in a tab that inherits
   * an address already over the threshold.
   */
  const [challengeRequired, setChallengeRequired] = useState(false);

  const config = useAppConfig();
  const siteKey = config.data?.turnstileSiteKey ?? null;
  const showChallenge = Boolean(siteKey) && challengeRequired;

  // A social sign-in that could not be completed comes back here as a code in
  // the query string — the Worker cannot render a message into the SPA, and
  // reflecting one from a provider into the page would be worse than a code.
  const socialError = oauthErrorMessage(searchParams.get('oauth_error'), searchParams.get('oauth_provider'));
  const from = (location.state as { from?: string } | null)?.from;
  const returnTo = sameOriginPath(from ?? null);
  /**
   * Where the WORKER asked us to come back to — today, the OAuth consent
   * screen, which cannot be reached before somebody is signed in.
   *
   * Only a path on this origin, and never a protocol-relative one (`//evil`
   * is a URL, not a path), so this cannot be turned into an open redirector by
   * anyone who can get a link clicked. It is followed with a full navigation
   * rather than the router, because the destination is served by the Worker
   * rather than being a screen this app knows about.
   */
  const nextUrl = sameOriginPath(searchParams.get('next'));
  // Sent here by the OAuth consent screen: an app (Zapier, Claude, ChatGPT…)
  // is waiting on the other side of this sign-in, so say so — otherwise a
  // person who came from their assistant lands on a bare sign-in page with no
  // idea why.
  const connectingApp = nextUrl?.startsWith('/api/oauth/authorize') ?? false;

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setFieldErrors({});

    try {
      const response = await authApi.login({
        email,
        password,
        rememberMe,
        ...(turnstileToken ? { turnstileToken } : {}),
      });

      // The password was right but the account is protected. No session exists
      // yet, and the type system is what stops this being treated as one.
      if (isMfaChallenge(response)) {
        setChallenge(response);
        return;
      }

      setSession(response);
      landAfterSignIn(response);
    } catch (caught) {
      setTurnstileReset((value) => value + 1);
      if (caught instanceof ApiRequestError) {
        setChallengeRequired(true);
        setError(caught.code === 'TURNSTILE_FAILED' ? t('auth.login.turnstileError') : caught.message);
        setFieldErrors(caught.fields);
      } else {
        setError(t('auth.login.networkError'));
      }
    } finally {
      setBusy(false);
    }
  }

  // Ask the Worker what the redirect left behind. The token itself never
  // reaches this code — it stays in a cookie the browser cannot read, and the
  // server substitutes it on the challenge and verify calls.
  useEffect(() => {
    if (!resumingSocial) return;
    let canceled = false;
    authApi
      .mfaPending()
      .then((pending) => {
        if (canceled) return;
        if (pending.pending && pending.options?.length) {
          setChallenge({
            mfaRequired: true,
            options: pending.options,
            trustedDeviceDays: pending.trustedDeviceDays ?? 0,
          });
        }
      })
      // Expired, or nothing waiting: the ordinary password form is the right
      // thing to show, which is what happens by falling through.
      .catch(() => undefined)
      .finally(() => {
        if (!canceled) setResumingSocial(false);
      });
    return () => {
      canceled = true;
    };
  }, [resumingSocial]);

  /**
   * Where a completed sign-in actually lands.
   *
   * Almost always wherever the person was heading. The exception is an
   * account the deployment requires a second factor from and which has none:
   * the server issues an ordinary session for it deliberately — refusing
   * would lock out every existing user the moment the mandate is turned on —
   * and marks it `mfaSetupRequired`, on the understanding that the client
   * finishes the job. Ignoring the flag, which is what this used to do, left
   * the mandate with no effect whatsoever for password sign-ins: the person
   * went straight to the dashboard with ordinary access and was never asked
   * to enroll.
   */
  function landAfterSignIn(session: { mfaSetupRequired?: boolean }) {
    if (session.mfaSetupRequired) {
      // The section draws its own "this account still needs a second factor"
      // notice from the same server state, so the hash is all this needs to say.
      navigate('/settings/security', { replace: true });
      return;
    }
    if (nextUrl) window.location.assign(nextUrl);
    else navigate(returnTo ?? '/', { replace: true });
  }

  if (challenge) {
    return (
      <MfaChallengeForm
        challenge={challenge}
        onVerified={(session) => {
          setSession(session);
          landAfterSignIn(session);
        }}
        onCancel={() => setChallenge(null)}
      />
    );
  }

  return (
    <AuthShell compact title={t('auth.login.title')} subtitle={t('auth.login.subtitle')}>
      {connectingApp ? (
        <div className="mb-4">
          <Alert tone="info">{t('auth.login.connectingApp')}</Alert>
        </div>
      ) : null}
      {socialError ? (
        <div className="mb-4">
          <Alert tone="error">{socialError}</Alert>
        </div>
      ) : null}

      <form onSubmit={handleSubmit} noValidate>
        {error ? (
          <div className="mb-4">
            <Alert tone="error">{error}</Alert>
          </div>
        ) : null}

        <TextInput
          label={t('auth.login.emailLabel')}
          type="email"
          name="email"
          autoComplete="username"
          required
          value={email}
          error={fieldErrors.email}
          onChange={(event) => setEmail(event.target.value)}
        />

        <TextInput
          label={t('auth.login.passwordLabel')}
          type="password"
          name="password"
          autoComplete="current-password"
          required
          value={password}
          error={fieldErrors.password}
          onChange={(event) => setPassword(event.target.value)}
        />

        <div className="-mt-2 mb-3 text-right text-sm">
          <Link to="/forgot-password" className="text-[color:var(--color-brand)] hover:underline">
            {t('auth.login.forgotPassword')}
          </Link>
        </div>

        <Checkbox
          label={t('auth.login.rememberMe')}
          hint={t('auth.login.rememberMeHint')}
          checked={rememberMe}
          onChange={(event) => setRememberMe(event.target.checked)}
        />

        {showChallenge && siteKey ? (
          <Turnstile siteKey={siteKey} onToken={setTurnstileToken} resetSignal={turnstileReset} />
        ) : null}

        <Button type="submit" variant="primary" busy={busy} className="w-full">
          {t('auth.login.submit')}
        </Button>
      </form>

      {/* Straight after Sign in, second only to it: a new company should find
          this without scrolling, on the smallest phones too. That is why the
          social buttons come after it rather than above the form. */}
      <div className="my-4 flex items-center gap-3">
        <span className="h-px flex-1 bg-[color:var(--color-line)]" aria-hidden="true" />
        <span className="max-w-[70%] text-center text-sm text-[color:var(--color-muted)]">
          {t('auth.login.newToBlastoClean')}
        </span>
        <span className="h-px flex-1 bg-[color:var(--color-line)]" aria-hidden="true" />
      </div>
      <Link to="/register" className="btn btn-tonal w-full">
        {t('auth.login.createAccountButton')}
      </Link>

      {/* The social buttons have to carry the same destination as the password
          form. A visitor sent here by the OAuth consent screen who signs in
          with Google must still return to the consent screen, rather than the
          app's default authenticated landing page. */}
      <SocialSignInButtons
        dividerAbove
        {...((nextUrl ?? returnTo) ? { returnTo: nextUrl ?? returnTo! } : {})}
        failTo={(nextUrl ?? returnTo) ? `/login?${new URLSearchParams({ next: nextUrl ?? returnTo! })}` : '/login'}
      />

      <div className="mt-6 flex flex-col gap-2 text-sm">
        <Link to="/delete-account" className="text-[color:var(--color-brand)] hover:underline">
          {t('auth.login.deleteAccount')}
        </Link>
      </div>
    </AuthShell>
  );
}

/**
 * "Verify it's you" — the second leg of a sign-in.
 *
 * Deliberately spare. Nothing here names the account, states the email address
 * or says which methods exist beyond the last four digits of a phone: somebody
 * holding a stolen password should learn nothing further from this screen.
 */
function MfaChallengeForm({
  challenge,
  onVerified,
  onCancel,
}: {
  challenge: MfaChallengeResponse;
  onVerified: (session: Awaited<ReturnType<typeof authApi.mfaVerify>>) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [option, setOption] = useState(challenge.options[0] ?? null);
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [rememberDevice, setRememberDevice] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** A verified sign-in held back one screen, to say how many codes are left. */
  const [recoveryWarning, setRecoveryWarning] = useState<{
    remaining: number;
    session: Awaited<ReturnType<typeof authApi.mfaVerify>>;
  } | null>(null);

  async function start(next: (typeof challenge.options)[number]) {
    setOption(next);
    setCode('');
    setError(null);
    setBusy(true);
    try {
      const started = await authApi.mfaChallenge({
        loginToken: challenge.loginToken,
        type: next.type,
        methodId: next.methodId,
      });
      setChallengeId(started.challengeId);
      // A security key has no code to type: the challenge and the prompt are
      // one step, so it runs here rather than waiting for a submit that has
      // nothing to submit.
      if (next.type === 'webauthn' && started.publicKey) {
        await finishWithKey(started.challengeId, started.publicKey);
      }
    } catch (caught) {
      setError(caught instanceof ApiRequestError ? caught.message : t('auth.mfa.error'));
    } finally {
      setBusy(false);
    }
  }

  /**
   * The security-key path.
   *
   * Without this the form asked somebody holding a hardware key to type a code
   * it cannot produce — the option was offered, chosen, and then impossible to
   * complete.
   */
  async function finishWithKey(id: string, publicKey: ServerPublicKeyRequest) {
    try {
      const assertion = await requestAssertion(publicKey);
      // Dismissed the browser prompt: they are still on the form, with the
      // other methods available. Nothing has gone wrong.
      if (!assertion) return;
      onVerified(
        await authApi.mfaVerify({ loginToken: challenge.loginToken, challengeId: id, assertion, rememberDevice }),
      );
    } catch (caught) {
      if (wasDismissed(caught)) return;
      // The challenge is spent or abandoned either way; a retry needs a new one.
      setChallengeId(null);
      setError(caught instanceof ApiRequestError ? caught.message : t('auth.mfa.error'));
    }
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    const chosen = option ?? challenge.options[0]!;
    if (!challengeId) {
      await start(chosen);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const session = await authApi.mfaVerify({
        loginToken: challenge.loginToken,
        challengeId,
        code,
        rememberDevice,
      });
      // A spent recovery code is the one outcome worth pausing on. The server
      // returns what is LEFT precisely so somebody can be told, and navigating
      // straight past it means a person can burn their last code and discover
      // it at the worst possible moment — the next time they are locked out.
      if (typeof session.recoveryCodesRemaining === 'number') {
        setRecoveryWarning({ remaining: session.recoveryCodesRemaining, session });
        return;
      }
      onVerified(session);
    } catch (caught) {
      setError(caught instanceof ApiRequestError ? caught.message : t('auth.mfa.error'));
    } finally {
      setBusy(false);
    }
  }

  if (recoveryWarning) {
    return (
      <AuthShell title={t('auth.mfa.recoveryUsedTitle')} subtitle={t('auth.mfa.recoveryUsedSubtitle')}>
        <Alert tone={recoveryWarning.remaining === 0 ? 'error' : 'warning'}>
          {t('auth.mfa.recoveryRemaining', { count: recoveryWarning.remaining })}
        </Alert>
        <Button type="button" className="mt-4 w-full" onClick={() => onVerified(recoveryWarning.session)}>
          {t('common.continue')}
        </Button>
      </AuthShell>
    );
  }

  return (
    <AuthShell title={t('auth.mfa.title')} subtitle={t('auth.mfa.subtitle')}>
      <form onSubmit={submit} noValidate>
        {error ? (
          <div className="mb-4">
            <Alert tone="error">{error}</Alert>
          </div>
        ) : null}

        <fieldset className="mb-4">
          <legend className="mb-2 text-sm font-medium">{t('auth.mfa.chooseMethod')}</legend>
          <div className="flex flex-col gap-2">
            {challenge.options.map((candidate) => (
              <label
                key={`${candidate.type}:${candidate.methodId ?? 'none'}`}
                className="flex items-center gap-2 text-sm"
              >
                <input
                  type="radio"
                  name="mfa-method"
                  checked={option?.methodId === candidate.methodId && option?.type === candidate.type}
                  onChange={() => void start(candidate)}
                />
                <span>
                  {candidate.type === 'sms' ? t('auth.mfa.textEnding', { label: candidate.label }) : candidate.label}
                </span>
              </label>
            ))}
          </div>
        </fieldset>

        {challengeId && option?.type !== 'webauthn' ? (
          <TextInput
            label={t('auth.mfa.codeLabel')}
            // A recovery code is Crockford base32 — `ABCD-EFGH-IJKL` — and on
            // the phone keyboards that show digits only, a numeric input mode
            // means the person cannot type the credential that exists for
            // exactly the moment they have lost everything else.
            inputMode={option?.type === 'recovery' ? 'text' : 'numeric'}
            autoComplete={option?.type === 'recovery' ? 'off' : 'one-time-code'}
            autoFocus
            value={code}
            onChange={(event) => setCode(event.target.value)}
          />
        ) : null}

        {option?.type === 'webauthn' ? (
          // A dismissed platform prompt leaves the challenge live and nothing
          // on screen to reopen it — and the already-selected radio fires no
          // `onChange`. For somebody whose only factor is that key, that meant
          // starting the whole sign-in again.
          <div className="mb-4">
            <Button type="button" variant="secondary" onClick={() => void start(option)} disabled={busy}>
              {t('auth.mfa.useSecurityKey')}
            </Button>
          </div>
        ) : null}

        {challenge.trustedDeviceDays > 0 ? (
          <Checkbox
            label={t('auth.mfa.rememberDevice', { days: challenge.trustedDeviceDays })}
            hint={t('auth.mfa.rememberDeviceHint')}
            checked={rememberDevice}
            onChange={(event) => setRememberDevice(event.target.checked)}
          />
        ) : null}

        {/* A security key answers through the platform prompt, so there is
            nothing to submit — the button above is its control. */}
        {option?.type !== 'webauthn' ? (
          <Button type="submit" variant="primary" busy={busy} className="w-full">
            {challengeId ? t('auth.mfa.verify') : t('auth.mfa.sendCode')}
          </Button>
        ) : null}
      </form>

      <div className="mt-6 text-sm">
        <button type="button" className="text-[color:var(--color-brand)] hover:underline" onClick={onCancel}>
          {t('auth.mfa.startOver')}
        </button>
      </div>
    </AuthShell>
  );
}
